import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from '../socket/SocketContext';
import { SOUND_MAP } from '../assets/soundMap';
import { isAppSoundEnabled, subscribeToAppSoundPreference } from '../utils/appSound';
import {
  isAlarmPopupEnabled,
  subscribeToAlarmPopupPreference,
} from '../utils/alarmPopupPreference';
import { useStatus } from './NotificationContext';

const POPUP_COOLDOWN_MS = Number(process.env.REACT_APP_ALARM_POPUP_COOLDOWN_MS || 45000);

const NON_POPUP_ALARM_TYPES = new Set([
  'device_reconnect',
  'device_disconnect',
  'device_disconnected',
  'device_connected',
  'device_online',
  'device_offline',
  'stopped',
  'vehicle_stopped',
  'idle',
  'external_alarm',
]);

const IGNITION_ON_TYPES = new Set(['ignition_on', 'engine_start', 'acc_on']);
const IGNITION_OFF_TYPES = new Set(['ignition_off', 'engine_stop', 'acc_off']);
const ALWAYS_POPUP_FRESH_TYPES = new Set(['sos_alarm', 'panic_button', 'external_power_cut']);

const ALARM_SOUND_MAP = {
  sos_alarm: SOUND_MAP.sos_emergency,
  panic_button: SOUND_MAP.sos_emergency,
  towing_alarm: SOUND_MAP.notification,
  unauthorized_movement: SOUND_MAP.notification,
  geofence_entry: SOUND_MAP.geofence_enter,
  geofence_exit: SOUND_MAP.fleet_geofence_exit,
  gps_signal_lost: SOUND_MAP.fleet_gps_lost,
  harsh_acceleration: SOUND_MAP.warning_double,
  harsh_braking: SOUND_MAP.warning_double,
  idle_too_long: SOUND_MAP.warning_double,
  low_battery: SOUND_MAP.warning_double,
  overspeed_alarm: SOUND_MAP.overspeed_alert,
  power_off: SOUND_MAP.power_cut,
  power_on: SOUND_MAP.power_restored,
  engine_start: SOUND_MAP.fleet_engine_start,
  engine_stop: SOUND_MAP.fleet_engine_stop,
  ignition_on: SOUND_MAP.fleet_engine_start,
  ignition_off: SOUND_MAP.fleet_engine_stop,
  fuel_theft: SOUND_MAP.tamper_alert,
  device_offline: SOUND_MAP.power_cut,
  speeding: SOUND_MAP.overspeed_alert,
  collision_alarm: SOUND_MAP.critical_triple,
  tampering: SOUND_MAP.tamper_alert,
  vibration_alarm: SOUND_MAP.notification,
};

function resolveAlarmSound(alarm) {
  const requestedSound = String(alarm?.sound || '').trim().toLowerCase();
  if (requestedSound === 'mute') return null;
  if (requestedSound && SOUND_MAP[requestedSound]) return SOUND_MAP[requestedSound];
  return ALARM_SOUND_MAP[alarm?.alarmType] || SOUND_MAP.notification || SOUND_MAP.default;
}

function formatMessage(alarm) {
  const name = alarm.deviceName || 'Unknown Device';
  const speed = alarm.data?.speed ?? alarm.data?.Speed ?? alarm.data?.speedKmh;
  const battery = alarm.data?.battery ?? alarm.data?.batteryPercent;

  const templates = {
    sos_alarm: `Emergency SOS triggered by ${name}. Immediate action required.`,
    panic_button: `Panic button activated by ${name}.`,
    towing_alarm: `${name} appears to be moving with ignition OFF. Possible towing or theft.`,
    unauthorized_movement: `${name} moved while Parking Mode was active. Possible theft attempt.`,
    vibration_alarm: `${name} vibration detected while parked.`,
    overspeed_alarm: `${name} overspeed detected${speed != null ? ` at ${speed} km/h` : ''}.`,
    speeding: `${name} is speeding${speed != null ? ` at ${speed} km/h` : ''}.`,
    low_battery: `${name} battery is low${battery != null ? ` (${battery}%)` : ''}.`,
    device_offline: `${name} is offline or not reporting.`,
    power_off: `External power disconnected from ${name}.`,
    power_on: `External power restored for ${name}.`,
    ignition_on: `${name} ignition turned ON.`,
    ignition_off: `${name} ignition turned OFF.`,
    default: `${alarm.alarmType} detected on ${name}.`,
  };

  return templates[alarm.alarmType] || templates.default;
}

function prettyAlarmLabel(value) {
  if (!value) return 'Unknown Alarm';
  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function toToastType(alarm) {
  const severity = String(alarm?.severity || '').toLowerCase();
  if (severity === 'critical' || severity === 'high') return 'error';
  if (severity === 'medium') return 'warning';
  return 'info';
}

const CenteredAlarmPopup = () => {
  const { connected, on } = useSocket();
  const { showStatus } = useStatus();
  const [isMuted, setIsMuted] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => isAppSoundEnabled());
  const [popupEnabled, setPopupEnabled] = useState(() => isAlarmPopupEnabled());

  const audioRef = useRef(null);
  const recentAlarmRef = useRef(new Map());
  const ignitionStateRef = useRef(new Map());
  const cooldownRef = useRef(new Map());

  useEffect(() => {
    const enable = () => {
      setSoundEnabled(isAppSoundEnabled());
      document.removeEventListener('click', enable);
      document.removeEventListener('keydown', enable);
      document.removeEventListener('touchstart', enable);
    };

    document.addEventListener('click', enable);
    document.addEventListener('keydown', enable);
    document.addEventListener('touchstart', enable);

    return () => {
      document.removeEventListener('click', enable);
      document.removeEventListener('keydown', enable);
      document.removeEventListener('touchstart', enable);
    };
  }, []);

  useEffect(() => {
    setSoundEnabled(isAppSoundEnabled());
    return subscribeToAppSoundPreference(setSoundEnabled);
  }, []);

  useEffect(() => {
    setPopupEnabled(isAlarmPopupEnabled());
    return subscribeToAlarmPopupPreference(setPopupEnabled);
  }, []);

  const stopSound = useCallback(() => {
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    } catch (_) {}
  }, []);

  const playSound = useCallback((alarm) => {
    if (isMuted || !soundEnabled || !isAppSoundEnabled()) return;

    try {
      const soundFile = resolveAlarmSound(alarm);
      if (!soundFile) return;

      stopSound();
      audioRef.current = new Audio(soundFile);
      audioRef.current.preload = 'auto';
      audioRef.current.volume = 0.8;
      audioRef.current.play().catch((err) => {
        console.warn('Alarm sound blocked:', err?.message || err);
      });
    } catch (_) {}
  }, [isMuted, soundEnabled, stopSound]);

  const normalizeAlarm = useCallback((data) => {
    try {
      let alarm = null;

      if (data?.alarm && typeof data.alarm === 'object') alarm = data.alarm;
      else if (data?.data?.alarm) alarm = data.data.alarm;
      else if (data?.alarmType && data?.imei) alarm = data;
      else if (data && data.alarmType) alarm = data;

      if (!alarm || !alarm.alarmType) return null;

      const lat = Number.isFinite(Number(alarm.latitude))
        ? Number(alarm.latitude)
        : Number.isFinite(Number(alarm.lat))
        ? Number(alarm.lat)
        : null;
      const lng = Number.isFinite(Number(alarm.longitude))
        ? Number(alarm.longitude)
        : Number.isFinite(Number(alarm.lng))
        ? Number(alarm.lng)
        : null;

      const normalizedId =
        alarm._id ||
        alarm.alarmId ||
        `${alarm.imei || 'unknown'}-${alarm.alarmType}-${alarm.timestamp || alarm.triggeredAt || Date.now()}`;

      const normalizedAlarmType = String(alarm.alarmType || '').trim().toLowerCase();
      const derivedAcc =
        ['ignition_on', 'acc_on', 'engine_start'].includes(normalizedAlarmType)
          ? true
          : ['ignition_off', 'acc_off', 'engine_stop'].includes(normalizedAlarmType)
          ? false
          : alarm.data?.accStatus ?? alarm.accStatus ?? alarm.acc;
      const derivedVehicleStatus =
        ['ignition_on', 'acc_on', 'engine_start'].includes(normalizedAlarmType)
          ? 'idle'
          : ['ignition_off', 'acc_off', 'engine_stop'].includes(normalizedAlarmType)
          ? 'stopped'
          : alarm.data?.vehicleStatus ?? alarm.vehicleStatus;

      return {
        _id: normalizedId,
        alarmId: normalizedId,
        alarmType: alarm.alarmType,
        severity: alarm.severity || 'medium',
        status: alarm.status || 'active',
        popupStatus: alarm.popupStatus,
        forcePopup: alarm.forcePopup,
        deviceId: alarm.deviceId || alarm.device?._id,
        deviceName: alarm.deviceName || alarm.device?.name || 'Unknown Device',
        imei: alarm.imei || alarm.device?.imei || '',
        triggeredAt: alarm.triggeredAt || alarm.timestamp || new Date().toISOString(),
        location:
          alarm.location ||
          alarm.data?.location ||
          (lat != null && lng != null
            ? {
                latitude: lat,
                longitude: lng,
              }
            : {}),
        data: {
          ...(alarm.data || {}),
          speed: alarm.data?.speed ?? alarm.speed,
          accStatus: derivedAcc,
          vehicleStatus: derivedVehicleStatus,
          heading: alarm.data?.heading ?? alarm.heading,
        },
      };
    } catch (_) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!connected) return;

    const handleNewAlarm = (data) => {
      if (!popupEnabled) return;

      const isStatusUpdate =
        data?.eventType === 'alarm_acknowledged' ||
        data?.eventType === 'alarm_resolved' ||
        data?.eventType === 'alarm_status_update';
      if (isStatusUpdate) return;

      const normalized = normalizeAlarm(data);
      if (!normalized) return;

      const normalizedType = String(normalized.alarmType || '').toLowerCase().trim();
      if (NON_POPUP_ALARM_TYPES.has(normalizedType)) return;

      const isIgnitionTransitionType =
        IGNITION_ON_TYPES.has(normalizedType) || IGNITION_OFF_TYPES.has(normalizedType);
      const ignitionState = IGNITION_ON_TYPES.has(normalizedType)
        ? 'on'
        : IGNITION_OFF_TYPES.has(normalizedType)
        ? 'off'
        : null;

      if (ignitionState) {
        const stateKey = normalized.imei || normalized.deviceId || 'unknown';
        const prevState = ignitionStateRef.current.get(stateKey);
        if (prevState === ignitionState) return;
        ignitionStateRef.current.set(stateKey, ignitionState);
      }

      const now = Date.now();
      const seenAt = recentAlarmRef.current.get(normalized._id);
      if (seenAt && now - seenAt < 15000) return;
      recentAlarmRef.current.set(normalized._id, now);

      if (recentAlarmRef.current.size > 500) {
        for (const [id, ts] of recentAlarmRef.current.entries()) {
          if (now - ts > 60000) recentAlarmRef.current.delete(id);
        }
      }

      const cooldownKey = `${normalized.imei || normalized.deviceId || 'unknown'}:${normalizedType}`;
      const lastFiredAt = cooldownRef.current.get(cooldownKey);
      if (!isIgnitionTransitionType && !ALWAYS_POPUP_FRESH_TYPES.has(normalizedType)) {
        if (lastFiredAt && now - lastFiredAt < POPUP_COOLDOWN_MS) return;
        cooldownRef.current.set(cooldownKey, now);
      }

      if (cooldownRef.current.size > 1000) {
        for (const [key, ts] of cooldownRef.current.entries()) {
          if (now - ts > Math.max(POPUP_COOLDOWN_MS, 60000)) cooldownRef.current.delete(key);
        }
      }

      const effectiveStatus = normalized.popupStatus || normalized.status;
      if (!normalized.forcePopup && effectiveStatus === 'suppressed') {
        return;
      }
      if (!normalized.forcePopup && (effectiveStatus === 'resolved' || effectiveStatus === 'acknowledged')) {
        return;
      }

      playSound(normalized);

      const locationLine =
        normalized.location?.address ||
        (normalized.location?.latitude != null && normalized.location?.longitude != null
          ? `Location: ${normalized.location.latitude}, ${normalized.location.longitude}`
          : '');
      const title = `${prettyAlarmLabel(normalized.alarmType)}${normalized.deviceName ? ` - ${normalized.deviceName}` : ''}`;
      const message = [formatMessage(normalized), locationLine].filter(Boolean).join('\n');

      showStatus(toToastType(normalized), { title, message }, { duration: 5000 });
    };

    const handleStatusUpdate = (data) => {
      const alarmId = data?.alarmId || data?._id;
      if (!alarmId) return;

      const status = data?.status || data?.eventType;
      const isFinal =
        status === 'resolved' ||
        status === 'acknowledged' ||
        status === 'alarm_resolved' ||
        status === 'alarm_acknowledged';
      if (!isFinal) return;

      recentAlarmRef.current.delete(String(alarmId));
    };

    const offAlarmEvent = on('alarm_event', handleNewAlarm);
    const offStatus = on('alarm_status_update', handleStatusUpdate);

    return () => {
      offAlarmEvent?.();
      offStatus?.();
    };
  }, [connected, normalizeAlarm, on, playSound, popupEnabled, showStatus]);

  useEffect(() => {
    if (!popupEnabled) stopSound();
  }, [popupEnabled, stopSound]);

  useEffect(() => () => {
    stopSound();
  }, [stopSound]);

  return null;
};

export default CenteredAlarmPopup;
